import type { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs';

import { createEffectFactory } from '@core/ngrx-legacy';
import type { ViewPositionPayload } from '@recruitee/views-types';

import { ViewListActions } from './view-list.store';
import { ViewsHttpService } from '../../services/views-http.service';
import { ViewActions } from '../view/view.store';

@Injectable({ providedIn: 'root' })
export class ViewListEffects {
  private readonly actions$ = inject(Actions);
  private readonly viewsHttpService = inject(ViewsHttpService);

  public effect = createEffectFactory(ViewListActions, this.actions$);

  public fetchOne$ = this.effect(ViewListActions.fetchCollection, ({ payload: { dataSourceId } }) =>
    this.viewsHttpService
      .fetchViewsList(dataSourceId)
      .pipe(map(data => ({ data, scope: dataSourceId }))),
  );

  public updateViewsPositions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ViewListActions.updateViewsPositions),
      switchMap(({ payload: { dataSourceId, positions } }) => {
        const positionsPayload = positions.map(
          ({ viewId, codename, position }): ViewPositionPayload =>
            !!codename ? { codename, position } : { viewId: <number>viewId, position },
        );
        return this.viewsHttpService.updateViewsPositions(dataSourceId, positionsPayload).pipe(
          map(views =>
            ViewListActions.updateViewsPositionsSuccess({
              payload: { views, dataSourceId },
            }),
          ),
          catchError((error: HttpErrorResponse) => [
            ViewListActions.updateViewsPositionsFailed({ payload: { error } }),
          ]),
        );
      }),
    ),
  );

  // TODO move
  public refresh$ = createEffect((actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        ViewActions.createOnePessimisticSuccess,
        ViewActions.removeOnePessimisticSuccess,
        ViewActions.updateOnePessimisticSuccess,
        ViewActions.duplicateSuccess,
      ),
      map(
        ({
          payload: {
            data: {
              view: { dataSourceId },
            },
          },
        }) => ViewListActions.fetchCollection({ payload: { dataSourceId } }),
      ),
    ),
  );
}
